// Team database: curated Palworld squad archetypes.
// Deterministic — every slot is resolved from the Pal roster by role score and
// element, so the database re-ranks automatically when Pal data changes.

import { PALS, type Element, type Pal } from "./pals-data";

export type TeamPurpose = "PvE" | "PvP" | "Boss" | "Base" | "Travel" | "Farming";

export interface TeamSlot {
  role: string;
  note: string;
  pal: Pal;
}

export interface TeamComp {
  slug: string;
  name: string;
  purpose: TeamPurpose;
  desc: string;
  tips: string[];
  slots: TeamSlot[];
}

type RoleField = "pveRating" | "pvpRating" | "baseRating" | "mountRating";

interface SlotSpec {
  role: string;
  note: string;
  field: RoleField;
  element?: Element;
  work?: string;
  rarity?: Pal["rarity"];
}

function resolve(specs: SlotSpec[]): TeamSlot[] {
  const used = new Set<number>();
  const slots: TeamSlot[] = [];
  for (const s of specs) {
    const pool = PALS.filter((p) => {
      if (used.has(p.id)) return false;
      if (s.element && p.element !== s.element && p.secondary !== s.element) return false;
      if (s.rarity && p.rarity !== s.rarity) return false;
      if (s.work && !p.work.includes(s.work)) return false;
      return true;
    });
    const fallbackPool = pool.length ? pool : PALS.filter((p) => !used.has(p.id));
    const pal = fallbackPool
      .slice()
      .sort((a, b) => b[s.field] - a[s.field] || a.name.localeCompare(b.name))[0];
    if (!pal) continue;
    used.add(pal.id);
    slots.push({ role: s.role, note: s.note, pal });
  }
  return slots;
}

const dps = (role: string, note: string, element?: Element): SlotSpec => ({ role, note, field: "pveRating", element });

type TeamSpec = Omit<TeamComp, "slots"> & { slots: SlotSpec[] };

const TEAM_SPECS: TeamSpec[] = [
  {
    slug: "all-round-pve",
    name: "All-Round PvE Squad",
    purpose: "PvE",
    desc: "A five-slot party that covers open-world combat, one flying mount and one gatherer so you never return to base mid-run.",
    tips: [
      "Keep one flier out while traversing — stamina regenerates while it is your active Pal.",
      "Swap your damage Pal to match the enemy element instead of levelling one Pal only.",
      "Carry Cakes so you can breed replacements without breaking the run.",
    ],
    slots: [
      dps("Main damage", "Highest raw PvE score in the roster — your default attacker."),
      dps("Element flex", "Second attacker with a different element so you cover resistances.", "Water"),
      { role: "Flying mount", note: "Travel and vertical scouting between towers.", field: "mountRating" },
      { role: "Gatherer", note: "Handles field harvesting and drops during the run.", field: "baseRating", work: "Gathering" },
      { role: "Tank", note: "Soaks boss aggro while your damage Pal reloads.", field: "pveRating", rarity: "Rare" },
    ],
  },
  {
    slug: "tower-boss-team",
    name: "Tower Boss Rush Team",
    purpose: "Boss",
    desc: "Built around burst damage windows: two element counters, a tank and a healer-style utility Pal for long tower fights.",
    tips: [
      "Tower bosses ignore capture — bring damage, not Spheres.",
      "Bosses telegraph in phases; swap Pals on the phase change to reset cooldowns.",
      "Pair with the DPS calculator to confirm your time-to-kill before entering.",
    ],
    slots: [
      dps("Primary counter", "Top attacker for the boss element you are fighting."),
      dps("Backup counter", "Second element so a resistant phase does not stall you.", "Dragon"),
      { role: "Aggro tank", note: "Highest defence body to hold the arena centre.", field: "pveRating", rarity: "Legendary" },
      { role: "Mount escape", note: "Fast mount for repositioning out of ground AoE.", field: "mountRating" },
      { role: "Support utility", note: "Partner skill that buffs damage or restores stamina.", field: "pvpRating" },
    ],
  },
  {
    slug: "pvp-ladder-team",
    name: "PvP Ladder Team",
    purpose: "PvP",
    desc: "Sustain-first ladder comp — cooldown-friendly attackers plus disruption, since PvP flattens one-shot burst.",
    tips: [
      "Sustained damage beats nukers in PvP because cooldowns are scaled.",
      "Lead with your disruptor to force the first swap.",
      "Condense your two carries to four stars before laddering.",
    ],
    slots: [
      { role: "Lead carry", note: "Best PvP score — consistent damage per cooldown.", field: "pvpRating" },
      { role: "Disruptor", note: "Status and slow pressure to force enemy swaps.", field: "pvpRating", element: "Dark" },
      { role: "Bruiser", note: "Mid-cost body that trades favourably into carries.", field: "pvpRating", element: "Ground" },
      { role: "Speed pick", note: "Outspeeds the mirror and finishes low-health targets.", field: "mountRating" },
      { role: "Closer", note: "Late-round finisher held in reserve.", field: "pveRating", rarity: "Legendary" },
    ],
  },
  {
    slug: "base-production-team",
    name: "Base Production Team",
    purpose: "Base",
    desc: "A no-idle production line: kindling, watering, planting, handiwork and transporting covered by five specialists.",
    tips: [
      "One Pal per suitability beats five generalists — throughput is per-task.",
      "Keep a Transporting Pal or your chests fill and the line stalls.",
      "Feed boxes near the ranch stop sanity loss on long sessions.",
    ],
    slots: [
      { role: "Kindling", note: "Runs the furnace and cooking pot.", field: "baseRating", work: "Kindling" },
      { role: "Watering", note: "Keeps berry and wheat plots irrigated.", field: "baseRating", work: "Watering" },
      { role: "Planting", note: "Reseeds plots so farming never idles.", field: "baseRating", work: "Planting" },
      { role: "Handiwork", note: "Fastest crafter for the assembly line.", field: "baseRating", work: "Handiwork" },
      { role: "Transporting", note: "Moves finished goods into chests.", field: "baseRating", work: "Transporting" },
    ],
  },
  {
    slug: "ore-coal-farming-team",
    name: "Ore & Coal Farming Team",
    purpose: "Farming",
    desc: "Mining outpost squad: two Mining Pals, a Transporting Pal, a Kindling smelter and a flying mount for hauling.",
    tips: [
      "Build the outpost inside the ore cluster so mining Pals never path away.",
      "Two miners plus one transporter is the cheapest stable ratio.",
      "Smelt on site — ingots weigh less than raw ore.",
    ],
    slots: [
      { role: "Miner", note: "Primary Mining suitability at the ore nodes.", field: "baseRating", work: "Mining" },
      { role: "Second miner", note: "Doubles node throughput on a coal outpost.", field: "baseRating", work: "Mining" },
      { role: "Transporter", note: "Feeds ore into the furnace and chests.", field: "baseRating", work: "Transporting" },
      { role: "Smelter", note: "Kindling Pal converting ore to ingots on site.", field: "baseRating", work: "Kindling" },
      { role: "Hauler mount", note: "Flying mount for the trip back to main base.", field: "mountRating" },
    ],
  },
  {
    slug: "travel-explorer-team",
    name: "Travel & Exploration Team",
    purpose: "Travel",
    desc: "Map-clearing party: the fastest flier, a swimmer, a glider-style mount and two capture-support Pals.",
    tips: [
      "Stamina depth matters more than top speed on long crossings.",
      "Keep a swimming mount for Sakurajima and Feybreak coastlines.",
      "Bring extra Spheres — exploration is when you meet Alphas.",
    ],
    slots: [
      { role: "Fastest flier", note: "Best ride speed in the roster.", field: "mountRating" },
      { role: "Swimmer", note: "Water crossings and coastal caves.", field: "mountRating", element: "Water" },
      { role: "Climber", note: "Ground mount for cliffs and mine interiors.", field: "mountRating", element: "Ground" },
      { role: "Capture support", note: "Weakens Alphas without killing them.", field: "pveRating" },
      { role: "Gatherer", note: "Picks up materials found en route.", field: "baseRating", work: "Gathering" },
    ],
  },
];

export const TEAMS: TeamComp[] = TEAM_SPECS.map((t) => ({ ...t, slots: resolve(t.slots) }));



export const TEAM_BY_SLUG: Record<string, TeamComp> = Object.fromEntries(
  TEAMS.map((t) => [t.slug, t]),
);

export const TEAM_PURPOSES: TeamPurpose[] = ["PvE", "PvP", "Boss", "Base", "Travel", "Farming"];

/** Coverage report for a user-assembled squad, used by the interactive builder. */
export function analyzeTeam(team: Pal[]) {
  const elements = new Set<Element>();
  const work = new Set<string>();
  for (const p of team) {
    elements.add(p.element);
    if (p.secondary) elements.add(p.secondary);
    for (const w of p.work) work.add(w);
  }
  const avg = (f: RoleField) =>
    team.length ? team.reduce((s, p) => s + p[f], 0) / team.length : 0;
  return {
    elements: [...elements],
    work: [...work].sort(),
    pve: avg("pveRating"),
    pvp: avg("pvpRating"),
    base: avg("baseRating"),
    mount: avg("mountRating"),
    hasMount: team.some((p) => p.mountRating >= 7),
  };
}
